import { useNavigate } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { ChevronDown } from "@/components/animate-ui/icons/chevron-down";
import { CircleX } from "@/components/animate-ui/icons/circle-x";
import { PlugZap } from "@/components/animate-ui/icons/plug-zap";
import {
	DropdownMenu,
	DropdownMenuCheckboxItem,
	DropdownMenuContent,
	DropdownMenuSeparator,
	DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { InputGroupButton } from "@/components/ui/input-group";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { getIntegrationIcon } from "@/lib/utils/integration-icons";
import { getToolsLabel, type Tool } from "@/lib/utils/task-form";

function AppIcon({ tool }: { tool: Tool }) {
	const iconData = getIntegrationIcon(tool.provider, tool.logoUrl);
	if (iconData.type === "logo") {
		return <img src={iconData.url} alt="" className="size-4 shrink-0 object-contain" />;
	}
	return <iconData.Icon className="size-4 shrink-0" />;
}

interface ToolsDropdownProps {
	selectedTools: string[];
	combinedTools: Tool[];
	onToggleTool: (toolId: string) => void;
}

/**
 * Dropdown menu for selecting integrations.
 *
 * Displays available integrations with active/inactive states. Inactive integrations
 * show a tooltip prompting users to set them up. Includes "Auto" option for
 * automatic integration selection.
 */
export function ToolsDropdown({ selectedTools, combinedTools, onToggleTool }: ToolsDropdownProps) {
	const navigate = useNavigate();
	const [toolSearch, setToolSearch] = useState("");

	const visibleTools = useMemo(
		() =>
			toolSearch
				? combinedTools.filter((t) => t.name.toLowerCase().includes(toolSearch.toLowerCase()))
				: combinedTools,
		[toolSearch, combinedTools]
	);

	return (
		<TooltipProvider>
			<DropdownMenu onOpenChange={(open) => !open && setToolSearch("")}>
				<DropdownMenuTrigger asChild>
					<InputGroupButton variant="ghost" type="button">
						<PlugZap className="mr-1.5 size-4" animateOnHover />
						{getToolsLabel(selectedTools, combinedTools)}
						<ChevronDown className="ml-1.5 size-4 opacity-50" animateOnHover />
					</InputGroupButton>
				</DropdownMenuTrigger>
				<DropdownMenuContent
					side="top"
					align="start"
					className="max-h-72 w-56 overflow-y-auto [--radius:0.95rem]"
				>
					<div className="sticky top-0 z-10 bg-popover p-2">
						<input
							type="text"
							placeholder="Search apps..."
							value={toolSearch}
							onChange={(e) => setToolSearch(e.target.value)}
							onKeyDown={(e) => e.stopPropagation()}
							className="w-full rounded-sm border border-input bg-transparent px-2 py-1 text-sm outline-none placeholder:text-muted-foreground focus:ring-1 focus:ring-ring"
						/>
					</div>
					<DropdownMenuSeparator />
					<DropdownMenuCheckboxItem
						checked={selectedTools.includes("auto")}
						onCheckedChange={() => onToggleTool("auto")}
					>
						Auto (recommended)
					</DropdownMenuCheckboxItem>
					<DropdownMenuSeparator />
					{visibleTools.length === 0 ? (
						<p className="px-2 py-1.5 text-sm text-muted-foreground">No apps found</p>
					) : (
						visibleTools.map((tool) => {
							const isActive = tool.isActive;

							if (!isActive) {
								return (
									<Tooltip key={tool.id}>
										<TooltipTrigger asChild>
											<div className="cursor-not-allowed hover:bg-accent/50 rounded-sm transition-colors">
												<DropdownMenuCheckboxItem
													checked={selectedTools.includes(tool.id)}
													onCheckedChange={undefined}
													disabled={true}
													className="opacity-60 cursor-not-allowed"
												>
													<div className="flex items-center gap-2">
														<AppIcon tool={tool} />
														<CircleX
															className="size-3.5 text-amber-500 animate-pulse"
															animateOnHover
														/>
														<span>{tool.name}</span>
													</div>
												</DropdownMenuCheckboxItem>
											</div>
										</TooltipTrigger>
										<TooltipContent side="right">
											<button
												type="button"
												onClick={(e) => {
													e.stopPropagation();
													navigate({
														to: "/integrations",
														search: {
															oauth: undefined,
															provider: undefined,
															message: undefined,
															composio_connection_id: undefined,
															composio_app_integration_id: undefined,
														},
													});
												}}
											>
												Setup {tool.name} <span className="underline">here</span>
											</button>
										</TooltipContent>
									</Tooltip>
								);
							}

							return (
								<DropdownMenuCheckboxItem
									key={tool.id}
									checked={selectedTools.includes(tool.id)}
									onCheckedChange={() => onToggleTool(tool.id)}
								>
									<div className="flex items-center gap-2">
										<AppIcon tool={tool} />
										<span>{tool.name}</span>
									</div>
								</DropdownMenuCheckboxItem>
							);
						})
					)}
				</DropdownMenuContent>
			</DropdownMenu>
		</TooltipProvider>
	);
}
